import { CountModeFields } from "../shared/CountModeFields";
import React, { useState } from "react";
import { AddActionButton } from "../AddActionButton";
import { ChipList } from "../ChipList";
import { ReportAccordionItem, preserveAnchorPosition } from "../ReportAccordion";
import { SelectPicker } from "../SelectPicker";
import { addChipValue, removeChipValue } from "../ValueMultiSelect";
import { ValuePicker } from "../ValuePicker";
import {
    DATE_GRANULARITY_OPTIONS,
    PLACEHOLDER_COLUMNS,
    allSelectableColumnsForModel,
    allColumnsAcrossModels,
    operatorForColumnChange,
    metricFilterColumns,
    reportTimeComparableColumns,
    metricFilterNodes,
    metricFilterLogic,
    setMetricFilterNodes,
    setMetricFilterLogic,
    metricFilterEnabled,
    metricFilterOutline,
    metricFilterIssues,
    breakdownColumnForModel,
    breakdownColumnsForModel,
    columnLabel,
    columnPickerLabel,
    isCalculatedColumnKey,
    columnMatchesKey,
    columnsForModel,
    dataColumnSelectionKey,
    detailColumnsForModel,
    isMetricReady,
    isTimestampColumnForModel,
    makeMetric,
    makeOtpRule,
    makeRuleCondition,
    nextId,
    ruleNodeIds,
    PERIOD_DATE_LABEL,
    enablePeriodDate,
    extraBreakdownLabel,
    isPeriodDateExtra,
    makePeriodDateCase,
    periodDateColumns,
    periodDateGranularities,
    periodDateIssues,
    periodDateNarrative,
    removePeriodDate,
    setPeriodDateGranularity,
    metricOverridesReportPeriod,
    makeMetricPeriodFilter,
    metricPeriodFilterLogic,
    metricPeriodFilterIssues,
    metricPeriodFilterOutline,
    detailRuleSetOutputs,
    selectedDetailRuleSetIds,
    usesDetailRuleSetList,
    withDetailRuleSetIds,
    withDetailRuleSetAlias,
    detailRuleSetAlias,
    detailRuleSetAliasText,
    detailRuleSetDisplayName,
    DETAIL_RULE_SET_ALIAS_MAX_LENGTH,
    MEASURE_AGGREGATES,
    calculatedFieldEnglish,
    calculatedFieldsForModel,
    numberCalculatedFieldsForModel,
    detailCalculatedColumns,
    measureIssue,
    DETAIL_CONSTANT_TYPES,
    MAX_DETAIL_CONSTANT_COLUMNS,
    DETAIL_CONSTANT_NAME_MAX_LENGTH,
    detailConstAlias,
    detailConstantIssue,
    usesCombinedRuleSets,
    switchBreakdownSource,
    switchDisplayAs,
} from "../../reportConfigurationLogic";
import { CombinedRuleSetsEditor } from "./CombinedRuleSetsEditor";
import type {
    BreakdownSource,
    CountMode,
    DataColumn,
    DataModel,
    DateGranularity,
    DisplayAs,
    ExtraBreakdown,
    DetailConstantColumn,
    DetailConstantValueType,
    MetricGroup,
    MetricMeasureAggregate,
    MetricSection,
    OtpCondition,
    OtpRule,
    PeriodDateCase,
    PlanConfig,
    ReportConfig,
    ReportConfigurationLookupOption,
} from "../../types";
import type { MetricFilterMode } from "../../reportConfigurationLogic";
import { InlineIcon, IconAction, StatusIcon } from "../shared/EditorControls";
import { RuleConditionEditor, RULE_LOGIC_OPTIONS, ruleLevelLogicPlaceholder, setRuleLevelUniformJoin } from "../shared/RuleConditionEditor";
import type { RuleTreeCtx } from "../shared/RuleConditionEditor";

// Mounted by the page so editor drafts and drag state survive tab/report switches.
export function useMetricsEditorState() {

    const [extraBreakdownSelections, setExtraBreakdownSelections] = useState<Record<string, string>>({});
    const [dragPayload, setDragPayload] = useState<{ value: string; metricId: string; extraId?: string } | null>(null);
    const [dropTarget, setDropTarget] = useState<string | null>(null);
    const [advancedMetricKeys, setAdvancedMetricKeys] = useState<Set<string>>(new Set());
    return { extraBreakdownSelections, setExtraBreakdownSelections, dragPayload, setDragPayload, dropTarget, setDropTarget, advancedMetricKeys, setAdvancedMetricKeys };
}

type MetricsEditorProps = {
    report: ReportConfig;
    // The plan this report belongs to. Needed for the metric client picker: its client is
    // always in scope and is shown selected and disabled there.
    plan?: PlanConfig;
    catalog: {
        metricDisplayOptions: ReportConfigurationLookupOption[];
        models: Record<string, DataModel>;
        breakdownSourceOptions: { id: string; value: BreakdownSource; name: string; }[];
        countModeOptions: { id: string; value: CountMode; name: string; }[];
        lookupOptionsForColumn: (columnKey: string, modelKey?: string | undefined) => ReportConfigurationLookupOption[];
        lookupLabelForColumn: (columnKey: string, value: string, modelKey?: string | undefined) => string;
        otpMissingHandlingOptions: ReportConfigurationLookupOption[];
        lookupOptionsForReportColumn: (report: ReportConfig, columnKey: string) => ReportConfigurationLookupOption[];
        lookupLabelForReportColumn: (report: ReportConfig, columnKey: string, value: string) => string;
        // Every active health plan's client, from the same ClientInfo list the backend
        // validates coPlanClientIds against. Values are client ids as strings.
        clientOptions: ReportConfigurationLookupOption[];
    };
    selection: {
        openMetricId: string;
        setOpenMetricId: React.Dispatch<React.SetStateAction<string>>;
        setOpenExcelMetricId: React.Dispatch<React.SetStateAction<string>>;
    };
    editing: {
        updateMetric: (metricId: string, updater: (metric: MetricSection) => MetricSection) => void;
        reserveUniqueId: (prefix: string, usedIds: Set<string>) => string;
        replaceSelectedReport: (updater: (report: ReportConfig) => ReportConfig) => void;
        revealAccordionItem: (id: string) => void;
        // Opens the Categorization tab with this rule set expanded (combined rule sets' "Open rule set").
        openRuleSet?: (ruleSetId: string) => void;
    };
    session: ReturnType<typeof useMetricsEditorState>;
};

export function MetricsEditor({ report, plan, catalog, selection, editing, session }: MetricsEditorProps) {
    const {
        metricDisplayOptions,
        models,
        breakdownSourceOptions,
        countModeOptions,
        lookupOptionsForColumn,
        lookupLabelForColumn,
        otpMissingHandlingOptions,
        lookupOptionsForReportColumn,
        lookupLabelForReportColumn,
        clientOptions,
    } = catalog;
    const { openMetricId, setOpenMetricId, setOpenExcelMetricId } = selection;
    const { updateMetric, reserveUniqueId, replaceSelectedReport, revealAccordionItem, openRuleSet } = editing;
    // One transient message per metric about the last Break Down Using / Show As switch
    // (e.g. "your combined setup is kept"). Not saved.
    const [combinedNotes, setCombinedNotes] = useState<Record<string, string>>({});
    const setCombinedNote = (sectionId: string, note: string) => setCombinedNotes((current) => ({ ...current, [sectionId]: note }));
    const { setDragPayload, setDropTarget, dropTarget, extraBreakdownSelections, setExtraBreakdownSelections, dragPayload, advancedMetricKeys, setAdvancedMetricKeys } = session;

    const advancedMetricKey = (sectionId: string) => `${report.id}::${sectionId}`;
    const isAdvancedOpen = (sectionId: string) => advancedMetricKeys.has(advancedMetricKey(sectionId));
    const setAdvancedOpen = (sectionId: string, open: boolean) => {
        const key = advancedMetricKey(sectionId);
        setAdvancedMetricKeys((current) => {
            const next = new Set(current);
            if (open) next.add(key);
            else next.delete(key);
            return next;
        });
    };

    function renderMetricsTab(report: ReportConfig) {
        if (!report.models.length) return <section className="rc-block" data-rc-validation="metrics-list"><h3>Metrics</h3><p className="rc-hint">Add a Data Model above first, then add metrics here. Pick a column and the query is ready.</p></section>;
        const firstAvailableColumn = allColumnsAcrossModels(report)[0];
        return (
            <section className="rc-block" data-rc-validation="metrics-list">
                <h3>Metrics</h3>
                <p className="rc-block-hint">Add a blank metric, then pick what to count by. Include, exclude, and grouping are all set independently for each metric.</p>
                <div className="rc-accordion-stack">
                    {report.sections.map((section, index) => {
                        const displayLabel = metricDisplayOptions.find((option) => option.value === section.displayAs)?.name || section.displayAs;
                        const breakdownLabel = section.breakdownColumn ? columnLabel(section.breakdownColumn) : "No column selected";
                        return (
                            <ReportAccordionItem
                                key={section.id}
                                id={`metric-${section.id}`}
                                eyebrow={`Metric ${index + 1}`}
                                title={section.name || "Untitled Metric"}
                                summary={`${displayLabel} - ${breakdownLabel}`}
                                meta={<StatusIcon ready={isMetricReady(report, section)} />}
                                open={openMetricId === section.id}
                                onOpenChange={(nextOpen) => {
                                    setOpenMetricId(nextOpen ? section.id : "");
                                    if (!nextOpen) setAdvancedOpen(section.id, false);
                                }}
                            >
                                {renderMetricCard(report, section)}
                            </ReportAccordionItem>
                        );
                    })}
                </div>
                <AddActionButton disabled={!firstAvailableColumn} onClick={() => {
                    const column = allColumnsAcrossModels(report)[0];
                    if (!column) return;
                    const usedIds = new Set(report.sections.map((section) => section.id));
                    const metric = makeMetric(column.model);
                    metric.id = reserveUniqueId("metric", usedIds);
                    setOpenMetricId(metric.id);
                    setOpenExcelMetricId(metric.id);
                    replaceSelectedReport((current) => ({ ...current, sections: [...current.sections, metric] }));
                    revealAccordionItem(`metric-${metric.id}`);
                }}>Add Metric</AddActionButton>
                {!firstAvailableColumn && <p className="rc-hint">No category columns are loaded for the selected Data Model yet.</p>}
            </section>
        );
    }

    /**
     * The ctx for one side of a metric's filters. Two things differ from a Categorization
     * rule: the column pool is every visible column on the section's model (so numeric and
     * date columns can be filtered on, not just category columns), and writes go through
     * setMetricFilterNodes, which keeps the legacy filterColumn/filterValues pair in sync.
     */
    function metricFilterTreeCtx(report: ReportConfig, section: MetricSection, mode: MetricFilterMode): RuleTreeCtx {
        return {
            scope: { ...report, models: section.model ? [section.model] : [] },
            columns: metricFilterColumns(section),
            // Same capability as a Categorization rule: a metric's criteria may compare two
            // columns or measure the minutes between two timestamps on its own model.
            timeColumns: reportTimeComparableColumns({ ...report, models: section.model ? [section.model] : [] }),
            nodes: metricFilterNodes(section, mode),
            logic: metricFilterLogic(section, mode),
            updateNodes: (updater) => updateMetric(section.id, (current) => setMetricFilterNodes(current, mode, updater(metricFilterNodes(current, mode)))),
            setRootLogic: (logic) => updateMetric(section.id, (current) => setMetricFilterLogic(current, mode, logic)),
            restrictOperatorsByType: true,
        };
    }

    function renderMetricCard(report: ReportConfig, section: MetricSection) {
        const allColumns = allColumnsAcrossModels(report);
        // The filter pool is every visible column on the model, not just the category ones:
        // the conditions now support numeric and date operators, which need numeric and date
        // columns to be selectable. The breakdown column is no longer excluded either - with
        // operators beyond "is any of", filtering on the same column you break down by is a
        // reasonable thing to want.
        const filterColumns = metricFilterColumns(section);
        const ready = isMetricReady(report, section);
        const advancedOpen = isAdvancedOpen(section.id);
        return (
            <div className="rc-groupbox" data-rc-validation={`metric-${section.id}`}>
                <div className="rc-metric-top">
                    <SelectPicker options={allColumns.map((column) => ({ id: `${column.model}-${column.key}`, value: column.key, name: columnPickerLabel(column) }))} value={section.breakdownColumn} placeholder="Select A Column..." searchPlaceholder="Search Columns..." onChange={(value) => {
                        const selected = allColumns.find((column) => column.key === value);
                        updateMetric(section.id, (current) => {
                            // The Period Date's columns and conditions belong to the old model, so
                            // switching model drops it (and its date-part breakdowns) rather than
                            // leaving rules that point at columns the new model does not have.
                            // Calculated fields are bound to ONE data model too, so a model switch also
                            // drops the measure and calculated columns that belonged to the old one.
                            const modelChanged = (selected?.model || "") !== current.model;
                            const base = modelChanged
                                ? { ...removePeriodDate(current), measure: null, detailCalculatedFieldIds: current.detailCalculatedFieldIds ? [] : undefined }
                                : current;
                            return { ...base, model: selected?.model || "", breakdownColumn: value, name: current.nameCustom ? current.name : value ? `Count By ${columnLabel(value)}` : "", include: [], exclude: [], groups: [] };
                        });
                    }} />
                    {section.breakdownColumn ? <span className="rc-generated-name"><InlineIcon icon="subdirectory_arrow_right" label="generates" /> <b>{section.name}</b></span> : <span className="rc-hint">Pick a column and the query is ready.</span>}
                    <span className="rc-spacer" />
                    <StatusIcon ready={ready} />
                    <IconAction label="Remove Metric" onClick={() => replaceSelectedReport((current) => ({ ...current, sections: current.sections.filter((item) => item.id !== section.id) }))} />
                </div>
                {section.breakdownColumn && (
                    <button
                        className={advancedOpen ? "rc-metric-options-toggle active" : "rc-metric-options-toggle"}
                        type="button"
                        onClick={(event) => {
                            const anchor = event.currentTarget.closest(".rc-accordion-item") || event.currentTarget;
                            preserveAnchorPosition(anchor, () => setAdvancedOpen(section.id, !advancedOpen));
                        }}
                    >
                        <span className="material-icons md-16" aria-hidden="true">tune</span>
                        <span>{advancedOpen ? "Hide Options" : "More Options"}</span>
                        <span className="material-icons md-16 rc-metric-options-chevron" aria-hidden="true">expand_more</span>
                    </button>
                )}
                {section.breakdownColumn && advancedOpen && (
                    <div className="rc-advanced">
                        <label className="rc-label-block">Name Shown in the Report<input value={section.name} onChange={(event) => updateMetric(section.id, (current) => ({ ...current, name: event.target.value, nameCustom: true }))} placeholder="Enter Metric Name" /></label>
                        {renderMetricClientPicker(section)}
                        <div className="rc-inline-fields metric-display-row">
                            <label className="rc-label-block">Show As<SelectPicker options={metricDisplayOptions.filter((option) => option.value !== "otp" || (section.model && models[section.model]?.otpEligible))} value={section.displayAs} onChange={(value) => {
                                // Combined tables are Count-only: another display falls back to one rule set and keeps the combined setup.
                                const result = switchDisplayAs(report, section, value as DisplayAs);
                                updateMetric(section.id, (current) => switchDisplayAs(report, current, value as DisplayAs).section);
                                if (result.note) setCombinedNote(section.id, result.note);
                            }} searchPlaceholder="Search Display Types..." /></label>
                            {(section.displayAs === "count" || section.displayAs === "percentage") && renderMeasurePicker(report, section)}
                            {section.displayAs !== "detail" && !((section.displayAs === "count" || section.displayAs === "percentage") && section.measure) && renderCountModePicker(section)}
                            {section.displayAs === "detail" && renderDetailRowModePicker(section)}
                        </div>
                        {renderCountModeHint(section)}
                        {renderMeasureHint(report, section)}
                        {renderMetricValueEditor(report, section)}
                        {renderExtraBreakdownBlock(section)}
                        {renderMetricPeriodFilterBlock(report, section, filterColumns)}
                        {renderPeriodDateBlock(report, section, filterColumns)}
                        {renderMetricFilters(report, section, filterColumns)}
                    </div>
                )}
            </div>
        );
    }

    // The report plan's own client, as the picker's value type (client ids are strings in
    // ReportConfigurationLookupOption). It is derived from the plan rather than read back
    // from the metric because only the EXTRA clients are stored on the section - see the
    // MetricSection note in types.ts and normalizeCoPlanClientIds in the save mapping.
    const planClientValue = plan?.clientId ? String(plan.clientId) : "";
    const lockedClientValues = planClientValue ? [planClientValue] : [];

    // Which health plans' rows this one metric counts: the report plan's client (always on,
    // shown selected and disabled) plus any Co-Plans the user adds. Both SQL builders union
    // the two at query time - sectionClientScope in logic/sqlPreview.ts for the preview and
    // ReportSqlBuilder.ClientScopeClause for the query that runs - so what is saved here is
    // only ever the extras.
    // "Apply Client Filter" (section.applyClientFilter, absent = on): when off the metric's
    // query carries no client predicate at all - no plan client, no Co-Plans - and the user
    // scopes it through the metric's own "only rows where" filters instead.
    function renderMetricClientPicker(section: MetricSection) {
        const appliesClientFilter = section.applyClientFilter !== false;
        const clientFilterToggle = (
            <label className="rc-cb rc-metric-client-toggle" title="When off, ClientId is left out of this metric's query entirely.">
                <input
                    type="checkbox"
                    checked={appliesClientFilter}
                    onChange={(event) => {
                        const checked = event.target.checked;
                        updateMetric(section.id, (current) => {
                            // Only "off" is stored; on is the default for every existing metric.
                            const next = { ...current };
                            delete next.applyClientFilter;
                            return checked ? next : { ...next, applyClientFilter: false };
                        });
                    }}
                /> Apply Client Filter
            </label>
        );
        if (!appliesClientFilter) {
            return (
                <div className="rc-metric-clients">
                    <p className="rc-lbl">Health Plans Counted in This Metric</p>
                    {clientFilterToggle}
                    <p className="rc-hint">The client filter is off: this metric's query has no ClientId condition, so it reads every client's records. Add a client condition under "Only rows where" if you want to limit it.</p>
                </div>
            );
        }
        if (!clientOptions.length) {
            return (
                <div className="rc-metric-clients">
                    <p className="rc-lbl">Health Plans Counted in This Metric</p>
                    {clientFilterToggle}
                </div>
            );
        }
        const coPlanValues = (section.coPlanClientIds || []).map(String).filter((value) => value !== planClientValue);
        const selectedValues = [...lockedClientValues, ...coPlanValues];
        const clientLabel = (value: string) => clientOptions.find((option) => option.value === value)?.name || value;
        const setCoPlans = (values: string[]) => updateMetric(section.id, (current) => ({
            ...current,
            coPlanClientIds: values
                .filter((value) => value !== planClientValue)
                .map((value) => Number(value))
                .filter((id) => Number.isInteger(id) && id > 0),
        }));
        // The plan's own client is never toggleable, in either direction - guarded here as
        // well as in ValuePicker/ChipList so no path can drop it from the selection.
        const toggleClient = (value: string) => {
            if (value === planClientValue) return;
            setCoPlans(coPlanValues.includes(value) ? coPlanValues.filter((item) => item !== value) : [...coPlanValues, value]);
        };
        return (
            <div className="rc-metric-clients">
                <p className="rc-lbl">Health Plans Counted in This Metric</p>
                {clientFilterToggle}
                <p className="rc-hint">
                    {planClientValue
                        ? `${clientLabel(planClientValue)} is this report's health plan and is always counted. Add Co-Plans to count their rows in this metric as well.`
                        : "This report's health plan has no client id, so its rows are matched by name. Add Co-Plans to count their rows in this metric as well."}
                </p>
                {/* No "Select All" here on purpose: onPickMany would widen a metric to every
                    health plan in one click, which is a scope change, not a convenience. */}
                <ValuePicker
                    ariaLabel="Add A Co-Plan To This Metric"
                    options={clientOptions}
                    placeholder="Add A Co-Plan..."
                    searchPlaceholder="Search Health Plans..."
                    selectedValues={selectedValues}
                    lockedValues={lockedClientValues}
                    selectedCount={selectedValues.length}
                    totalCount={clientOptions.length}
                    onPick={toggleClient}
                    onToggle={toggleClient}
                    onClear={() => setCoPlans([])}
                />
                <ChipList
                    values={selectedValues}
                    onRemove={toggleClient}
                    getLabel={clientLabel}
                    lockedValues={lockedClientValues}
                    lockedTitle="This report's own health plan - always counted."
                    emptyText="No health plans selected."
                />
            </div>
        );
    }

    function renderBreakdownSourcePicker(section: MetricSection) {
        if (!section.model) return null;
        return (
            <label className="rc-label-block">
                Break Down Using
                <SelectPicker
                    // "Several Rule Sets" is a Count-only option; a metric already using it always
                    // sees it, so a saved configuration is never shown with a blank picker.
                    options={(breakdownSourceOptions.some((option) => option.value === "ruleSets") || section.breakdownSource !== "ruleSets"
                        ? breakdownSourceOptions
                        : [...breakdownSourceOptions, { id: "ruleSets", value: "ruleSets" as BreakdownSource, name: "Several Rule Sets - Combined Table" }])
                        .filter((option) => option.value !== "ruleSets" || section.displayAs === "count" || section.breakdownSource === "ruleSets")}
                    value={section.breakdownSource}
                    onChange={(value) => {
                        const result = switchBreakdownSource(report, section, value as BreakdownSource);
                        updateMetric(section.id, (current) => switchBreakdownSource(report, current, value as BreakdownSource).section);
                        setCombinedNote(section.id, result.note);
                    }}
                    searchPlaceholder="Search..."
                />
            </label>
        );
    }

    function renderCountModePicker(section: MetricSection) {
        if (!section.model) return null;
        const columns = allSelectableColumnsForModel(section.model);
        const countMode = section.countMode || "all";
        return (
            <CountModeFields
                countMode={countMode}
                countModeOptions={countModeOptions}
                getColumnOptions={() => columns.map((column) => ({ id: dataColumnSelectionKey(column), value: dataColumnSelectionKey(column), name: column.label, sortOrder: column.sortOrder }))}
                distinctColumn={section.countDistinctColumn}
                validationField={`metric-count-${section.id}`}
                onCountModeChange={(value) => updateMetric(section.id, (current) => ({ ...current, countMode: value as CountMode, countDistinctColumn: value === "all" ? "" : current.countDistinctColumn || "" }))}
                onDistinctColumnChange={(value) => updateMetric(section.id, (current) => ({ ...current, countDistinctColumn: value }))}
            />
        );
    }

    // Count / Percentage: what the figure is - a row count, or Sum / Average / Min / Max of a
    // calculated field (Calculated Fields tab). Percentage only offers Sum: a share of an
    // average has no meaning. Picking an aggregate hides the Count picker, which no longer applies.
    function renderMeasurePicker(report: ReportConfig, section: MetricSection) {
        if (!section.model) return null;
        const aggregates = section.displayAs === "percentage" ? MEASURE_AGGREGATES.filter((item) => item.key === "sum") : MEASURE_AGGREGATES;
        // Only number fields can be added up; text fields are Full Row Detail columns.
        const fields = numberCalculatedFieldsForModel(report, section.model);
        const modelName = models[section.model]?.label || section.model;
        const measureOptions = [
            { id: "count", value: "count", name: section.displayAs === "percentage" ? "Share of Rows" : "Count of Rows" },
            ...aggregates.map((item) => ({ id: item.key, value: item.key, name: `${item.label} a Calculated Field` })),
        ];
        return (
            <>
                <label className="rc-label-block" data-rc-validation={`metric-measure-${section.id}`}>
                    Measure
                    <SelectPicker
                        options={measureOptions}
                        value={section.measure?.aggregate || "count"}
                        searchable={false}
                        onChange={(value) => updateMetric(section.id, (current) => ({
                            ...current,
                            measure: value === "count" ? null : { aggregate: value as MetricMeasureAggregate, fieldId: current.measure?.fieldId || fields[0]?.id || "" },
                        }))}
                    />
                </label>
                {section.measure && (
                    <label className="rc-label-block">
                        Calculated Field
                        <SelectPicker
                            options={fields.map((field) => ({ id: field.id, value: field.id, name: field.name || "Untitled Calculated Field" }))}
                            value={fields.some((field) => field.id === section.measure?.fieldId) ? section.measure.fieldId : ""}
                            placeholder={fields.length ? `Choose a ${modelName} Field...` : `No Calculated Fields On ${modelName}`}
                            searchPlaceholder="Search Fields..."
                            onChange={(value) => updateMetric(section.id, (current) => ({ ...current, measure: { aggregate: current.measure?.aggregate || "sum", fieldId: value } }))}
                        />
                    </label>
                )}
            </>
        );
    }

    function renderMeasureHint(report: ReportConfig, section: MetricSection) {
        if (!section.measure || (section.displayAs !== "count" && section.displayAs !== "percentage")) return null;
        const issue = measureIssue(report, section);
        if (issue) return <p className="rc-hint">{issue}{numberCalculatedFieldsForModel(report, section.model).length ? "" : " Create a number field on the Calculated Fields tab."}</p>;
        const field = numberCalculatedFieldsForModel(report, section.model).find((item) => item.id === section.measure?.fieldId);
        return <p className="rc-hint">Each row's {field?.name} = {calculatedFieldEnglish(field)}. Blank results are left out of the {section.measure.aggregate === "avg" ? "average" : section.measure.aggregate === "sum" ? "total" : "comparison"}.{section.measure.aggregate !== "sum" ? " On the Excel sheet, set the total row and total column to Average (or turn them off) - adding up averages, minimums or maximums gives a misleading total." : ""}</p>;
    }

    // Full Row Detail: calculated fields shown as extra columns ("detail:calc:<id>"), after
    // the categorization columns. Order here is the default sheet order; headers, order and
    // number format can be changed on the Excel Layout tab like any other column.
    function renderDetailCalculatedColumns(report: ReportConfig, section: MetricSection) {
        const selectedIds = section.detailCalculatedFieldIds || [];
        const columns = detailCalculatedColumns(report, section);
        const available = calculatedFieldsForModel(report, section.model).filter((field) => !selectedIds.includes(field.id));
        const setIds = (ids: string[]) => updateMetric(section.id, (current) => ({ ...current, detailCalculatedFieldIds: ids }));
        const move = (index: number, direction: -1 | 1) => {
            const target = index + direction;
            if (target < 0 || target >= selectedIds.length) return;
            const next = [...selectedIds];
            [next[index], next[target]] = [next[target], next[index]];
            setIds(next);
        };
        return (
            <div className="rc-info-box rc-detail-rulesets" data-rc-validation={`metric-detail-calc-${section.id}`}>
                <p className="rc-lbl">Calculated Columns</p>
                <p className="rc-hint">Add calculated fields from the Calculated Fields tab as extra columns. Each is worked out for every row; the column header is the field's name. Only fields built on <b>{models[section.model]?.label || section.model}</b> - this metric's data model - can be added.</p>
                {columns.length ? (
                    <ol className="rc-detail-ruleset-list">
                        {columns.map((item, index) => (
                            <li className="rc-detail-ruleset-item" key={item.fieldId}>
                                <span className="rc-detail-ruleset-order">{index + 1}</span>
                                <span className="rc-detail-ruleset-copy">
                                    <b>{item.field ? item.label : "Missing calculated field"}</b>
                                    <small>{!item.field ? "This field no longer exists." : item.expr ? calculatedFieldEnglish(item.field) : item.field.model !== section.model ? "Calculated on a different data model - remove it." : "Not finished yet - complete it on the Calculated Fields tab."}</small>
                                </span>
                                <button className="rc-minibtn" type="button" aria-label={`Move ${item.label} up`} disabled={index === 0} onClick={() => move(index, -1)}>Up</button>
                                <button className="rc-minibtn" type="button" aria-label={`Move ${item.label} down`} disabled={index === columns.length - 1} onClick={() => move(index, 1)}>Down</button>
                                <IconAction label={`Remove ${item.label}`} onClick={() => setIds(selectedIds.filter((id) => id !== item.fieldId))} />
                            </li>
                        ))}
                    </ol>
                ) : <p className="rc-hint">{calculatedFieldsForModel(report, section.model).length ? "No calculated columns added." : "No calculated fields exist for this data model yet. Create one on the Calculated Fields tab."}</p>}
                {available.length > 0 && (
                    <SelectPicker
                        options={[{ id: "none", value: "", name: "Add a Calculated Field..." }, ...available.map((field) => ({ id: field.id, value: field.id, name: field.name || "Untitled Calculated Field" }))]}
                        value=""
                        onChange={(value) => { if (value) setIds([...selectedIds, value]); }}
                        searchPlaceholder="Search Fields..."
                    />
                )}
            </div>
        );
    }

    // Full Row Detail: fixed value columns ("detail:const:<id>") - the same text, number or date
    // on every row, after the calculated columns. The server binds each value as a typed
    // parameter. Renaming a column also renames its sheet header unless the header was
    // customized on the Excel Layout tab.
    function renderDetailConstantColumns(section: MetricSection) {
        const columns = section.detailConstantColumns || [];
        const setColumns = (updater: (items: DetailConstantColumn[]) => DetailConstantColumn[]) =>
            updateMetric(section.id, (current) => ({ ...current, detailConstantColumns: updater(current.detailConstantColumns || []) }));
        const updateColumn = (id: string, updater: (item: DetailConstantColumn) => DetailConstantColumn) =>
            setColumns((items) => items.map((item) => item.id === id ? updater(item) : item));
        const rename = (column: DetailConstantColumn, name: string) => updateMetric(section.id, (current) => {
            const next = { ...current, detailConstantColumns: (current.detailConstantColumns || []).map((item) => item.id === column.id ? { ...item, name } : item) };
            if (!next.excelLayout) return next;
            const previous = (column.name || "").trim();
            return {
                ...next,
                excelLayout: {
                    ...next.excelLayout,
                    columns: next.excelLayout.columns.map((layoutColumn) => {
                        if (layoutColumn.sourceAlias !== detailConstAlias(column.id)) return layoutColumn;
                        const header = layoutColumn.detail?.header?.trim() || "";
                        const followsName = !header || header === previous;
                        return { ...layoutColumn, label: name.trim(), ...(layoutColumn.detail && followsName ? { detail: { ...layoutColumn.detail, header: name.trim() } } : {}) };
                    }),
                },
            };
        });
        const move = (index: number, direction: -1 | 1) => setColumns((items) => {
            const target = index + direction;
            if (target < 0 || target >= items.length) return items;
            const next = [...items];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
        const typeOptions = DETAIL_CONSTANT_TYPES.map((item) => ({ id: item.key, value: item.key, name: item.label }));
        return (
            <div className="rc-info-box rc-detail-rulesets" data-rc-validation={`metric-detail-const-${section.id}`}>
                <p className="rc-lbl">Fixed Value Columns</p>
                <p className="rc-hint">Add columns that show the same value on every row - for example a Program column that is always "NEMT", a Rate of 12.50, or a Contract Start date. Leave the value blank for an empty column.</p>
                {columns.length > 0 ? (
                    <ol className="rc-detail-ruleset-list">
                        {columns.map((column, index) => {
                            const issue = detailConstantIssue(section, column);
                            return (
                                <li className={issue ? "rc-detail-const-item has-issue" : "rc-detail-const-item"} key={column.id}>
                                    <span className="rc-detail-ruleset-order">{index + 1}</span>
                                    <label className="rc-detail-ruleset-alias">
                                        <span>Column name</span>
                                        <input
                                            value={column.name}
                                            placeholder="e.g. Program"
                                            maxLength={DETAIL_CONSTANT_NAME_MAX_LENGTH}
                                            aria-label={`Name of fixed value column ${index + 1}`}
                                            onChange={(event) => rename(column, event.target.value)}
                                        />
                                    </label>
                                    <label className="rc-detail-ruleset-alias">
                                        <span>Type</span>
                                        <SelectPicker
                                            options={typeOptions}
                                            value={column.valueType}
                                            searchable={false}
                                            onChange={(value) => {
                                                if (value === column.valueType) return;
                                                // A value typed for one type rarely fits another, so it starts empty.
                                                updateMetric(section.id, (current) => {
                                                    const next = { ...current, detailConstantColumns: (current.detailConstantColumns || []).map((item) => item.id === column.id ? { ...item, valueType: value as DetailConstantValueType, value: "" } : item) };
                                                    if (!next.excelLayout) return next;
                                                    const format = value === "number" ? "decimal2" as const : value === "date" ? "MM-dd-yyyy" as const : "text" as const;
                                                    return { ...next, excelLayout: { ...next.excelLayout, columns: next.excelLayout.columns.map((layoutColumn) => layoutColumn.sourceAlias === detailConstAlias(column.id) && layoutColumn.detail ? { ...layoutColumn, detail: { ...layoutColumn.detail, format } } : layoutColumn) } };
                                                });
                                            }}
                                        />
                                    </label>
                                    <label className="rc-detail-ruleset-alias">
                                        <span>Value</span>
                                        <input
                                            type={column.valueType === "date" ? "date" : "text"}
                                            inputMode={column.valueType === "number" ? "decimal" : undefined}
                                            value={column.value}
                                            placeholder={column.valueType === "number" ? "e.g. 12.50" : column.valueType === "date" ? "" : "e.g. NEMT"}
                                            aria-label={`Value of fixed value column ${index + 1}`}
                                            onChange={(event) => updateColumn(column.id, (item) => ({ ...item, value: event.target.value }))}
                                        />
                                    </label>
                                    <button className="rc-minibtn" type="button" aria-label={`Move ${column.name || "column"} up`} disabled={index === 0} onClick={() => move(index, -1)}>Up</button>
                                    <button className="rc-minibtn" type="button" aria-label={`Move ${column.name || "column"} down`} disabled={index === columns.length - 1} onClick={() => move(index, 1)}>Down</button>
                                    <IconAction label={`Remove ${column.name || "column"}`} onClick={() => setColumns((items) => items.filter((item) => item.id !== column.id))} />
                                    {issue && <span className="rc-detail-const-issue">{issue}</span>}
                                </li>
                            );
                        })}
                    </ol>
                ) : <p className="rc-hint">No fixed value columns added.</p>}
                <AddActionButton
                    className="compact"
                    disabled={columns.length >= MAX_DETAIL_CONSTANT_COLUMNS}
                    title={columns.length >= MAX_DETAIL_CONSTANT_COLUMNS ? `A metric can have at most ${MAX_DETAIL_CONSTANT_COLUMNS} fixed value columns.` : undefined}
                    onClick={() => setColumns((items) => [...items, { id: nextId("const"), name: "", valueType: "text", value: "" }])}
                >
                    Add Fixed Value Column
                </AddActionButton>
                {columns.length > 0 && <p className="rc-hint">They are added after the other columns. Set their order, headers and format on the Excel Layout tab.</p>}
            </div>
        );
    }

    // Full Row Detail counterpart of the Count Mode picker, in the same slot next to "Show As":
    // all matching rows, or each distinct row once (SELECT DISTINCT).
    function renderDetailRowModePicker(section: MetricSection) {
        if (!section.model) return null;
        return (
            <label className="rc-label-block" data-rc-validation={`metric-detail-distinct-${section.id}`}>
                Rows to Return
                <SelectPicker
                    options={[
                        { id: "all", value: "all", name: "All matching rows" },
                        { id: "distinct", value: "distinct", name: "Unique rows only (DISTINCT)" },
                    ]}
                    value={section.detailDistinct ? "distinct" : "all"}
                    onChange={(value) => updateMetric(section.id, (current) => ({ ...current, detailDistinct: value === "distinct" }))}
                    searchPlaceholder="Search..."
                />
            </label>
        );
    }

    function renderCountModeHint(section: MetricSection) {
        if (section.displayAs === "detail" && section.detailDistinct) {
            return <p className="rc-hint">Unique rows only: rows that have the same value in every returned column - including categorization columns - are shown once. Choose fewer columns to collapse more rows.</p>;
        }
        if (section.displayAs === "detail" || (section.countMode || "all") !== "distinct" || section.countDistinctColumn) return null;
        return <p className="rc-hint">Distinct counting is on, but no column is chosen yet - the query still uses COUNT(*) until one is picked.</p>;
    }

    function renderMetricValueEditor(report: ReportConfig, section: MetricSection) {
        const options = lookupOptionsForColumn(section.breakdownColumn, section.model);
        // A calculated field has no lookup list (only a date part's fixed values): its values
        // are typed in the pickers below instead.
        const calculatedBreakdown = isCalculatedColumnKey(section.breakdownColumn);
        const values = options.map((option) => option.value);
        const groupedValues = new Set<string>();
        section.groups.forEach((group) => group.values.forEach((value) => groupedValues.add(value)));
        const unusedValues = values.filter((value) => !groupedValues.has(value));
        const usesRuleSet = section.breakdownSource === "ruleSet";
        // Combined rule sets replace the value lists and grouping below: the breakdown column is
        // only the ELSE fallback there, so its values don't decide anything.
        if (usesCombinedRuleSets(section)) {
            return (
                <>
                    {renderBreakdownSourcePicker(section)}
                    <CombinedRuleSetsEditor
                        report={report}
                        section={section}
                        updateMetric={updateMetric}
                        onOpenRuleSet={(ruleSetId) => openRuleSet?.(ruleSetId)}
                        note={combinedNotes[section.id] || ""}
                        onDismissNote={() => setCombinedNote(section.id, "")}
                        onNote={(note) => setCombinedNote(section.id, note)}
                    />
                </>
            );
        }
        const note = combinedNotes[section.id];
        const includeKey = dropKey("include", section.id);
        const excludeKey = dropKey("exclude", section.id);
        const newGroupKey = dropKey("newgroup", section.id);
        return (
            <>
                {note && (
                    <div className="rc-combined-note" role="status">
                        <span className="material-icons md-16" aria-hidden="true">info</span>
                        <span>{note}</span>
                        <button type="button" className="rc-iconbtn muted" aria-label="Dismiss" title="Dismiss" onClick={() => setCombinedNote(section.id, "")}>
                            <span className="material-icons md-16" aria-hidden="true">close</span>
                        </button>
                    </div>
                )}
                {section.displayAs === "detail" ? renderDetailRuleSetPicker(report, section) : (
                    <>
                        {renderBreakdownSourcePicker(section)}
                        {usesRuleSet && renderMetricRulePicker(report, section)}
                        {usesRuleSet && <p className="rc-hint">If the rule set creates Category, that category becomes this metric's row label. If it targets a real column, it recodes that column's values.</p>}
                    </>
                )}
                {section.displayAs === "detail" && renderDetailColumns(section)}
                {section.displayAs === "detail" && renderDetailCalculatedColumns(report, section)}
                {section.displayAs === "detail" && renderDetailConstantColumns(section)}
                <div className="rc-value-panel">
                    <div className="rc-value-panel-head">
                        <span>Available Values</span>
                        <span>{options.length}</span>
                    </div>
                    <p className="rc-src">{calculatedBreakdown
                        ? options.length
                            ? "Calculated field - every value it can take is listed. Type any other value in the pickers below."
                            : "Calculated field - its values are worked out for each row, so there is no list. Type the values to include, leave out or group in the pickers below."
                        : PLACEHOLDER_COLUMNS.has(section.breakdownColumn) ? `Sample values shown - in production this is SELECT DISTINCT ${section.breakdownColumn} from the real table.` : "Values loaded via SELECT DISTINCT against the data model."}</p>
                    {options.length ? (
                        <div className="rc-palette">{options.map((option) => {
                            const value = option.value;
                            const used = section.include.includes(value) || section.exclude.includes(value) || groupedValues.has(value);
                            return (
                                <span
                                    className={used ? "rc-chip rc-draggable rc-used" : "rc-chip rc-draggable"}
                                    draggable
                                    key={option.id}
                                    onClick={() => updateMetric(section.id, (current) => ({ ...current, include: addChipValue(current.include, value), exclude: removeChipValue(current.exclude, value) }))}
                                    onDragEnd={() => { setDragPayload(null); setDropTarget(null); }}
                                    onDragStart={() => setDragPayload({ value, metricId: section.id })}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {option.name}
                                </span>
                            );
                        })}</div>
                    ) : <div className="rc-empty-values">{calculatedBreakdown ? "No fixed values for this calculated field." : "No lookup values returned for this column."}</div>}
                </div>
                <div className="rc-two-cols">
                    <div>
                        <p className="rc-lbl">Include Only These Breakdown Values</p>
                        <p className="rc-hint">Drag values here to keep just those values of the breakdown column. Leave empty to include everything. For conditions over other columns, or for AND/OR and nested groups, use Inclusion Criteria below.</p>
                        <div className={dropTarget === includeKey ? "rc-dropzone rc-over" : "rc-dropzone"} {...dropZoneProps(includeKey, () => dropMetricValue(section.id, "include"))}>
                            <ChipList values={section.include} onRemove={(value) => updateMetric(section.id, (current) => ({ ...current, include: removeChipValue(current.include, value) }))} emptyText="Drop Values Here" getLabel={(value) => lookupLabelForColumn(section.breakdownColumn, value, section.model)} />
                        </div>
                        {calculatedBreakdown && <ValuePicker allowCustom options={options.filter((option) => !section.include.includes(option.value))} onPick={(value) => updateMetric(section.id, (current) => ({ ...current, include: addChipValue(current.include, value), exclude: removeChipValue(current.exclude, value) }))} />}
                        <div className="rc-mini-actions"><button className="rc-minibtn" onClick={() => updateMetric(section.id, (current) => ({ ...current, include: values }))}>Include Everything</button><button className="rc-minibtn" onClick={() => updateMetric(section.id, (current) => ({ ...current, include: [] }))}>Clear</button></div>
                    </div>
                    <div>
                        <p className="rc-lbl">Leave Out These Breakdown Values</p>
                        <p className="rc-hint">Drag values here to leave just those values of the breakdown column out, while including everything else. For anything richer, use Exclusion Criteria below.</p>
                        <div className={dropTarget === excludeKey ? "rc-dropzone rc-over" : "rc-dropzone"} {...dropZoneProps(excludeKey, () => dropMetricValue(section.id, "exclude"))}>
                            <ChipList values={section.exclude} onRemove={(value) => updateMetric(section.id, (current) => ({ ...current, exclude: removeChipValue(current.exclude, value) }))} emptyText="Drop Values Here" getLabel={(value) => lookupLabelForColumn(section.breakdownColumn, value, section.model)} />
                        </div>
                        <ValuePicker allowCustom={calculatedBreakdown} options={options.filter((option) => !section.exclude.includes(option.value))} onPick={(value) => updateMetric(section.id, (current) => ({ ...current, exclude: addChipValue(current.exclude, value), include: removeChipValue(current.include, value) }))} />
                    </div>
                </div>
                {section.displayAs === "otp" && renderOtpRules(section)}
                {section.displayAs === "detail" ? (
                    <p className="rc-hint">Grouping does not apply to full row detail. Use the include and leave-out selections above to filter the returned rows.</p>
                ) : section.displayAs !== "otp" || !section.noBreakdown ? (
                    <div className="rc-grouping"><p className="rc-lbl">Group Similar Values Together</p><p className="rc-hint">Drag values onto a box to combine them into one label. Anything not grouped still shows up on its own.</p>{section.groups.map((group) => renderMetricGroup(section, group))}<div className={dropTarget === newGroupKey ? "rc-newgroupzone rc-over" : "rc-newgroupzone"} {...dropZoneProps(newGroupKey, () => dropMetricValue(section.id, "newgroup"))} onClick={() => updateMetric(section.id, (current) => ({ ...current, groups: [...current.groups, { id: nextId("group"), name: "New Group", values: unusedValues.slice(0, 1) }] }))} role="button" tabIndex={0}>+ Drop A Value Here To Start A New Group</div>{section.groups.length > 0 && unusedValues.length > 0 && <div className="rc-leftoverbox"><p className="rc-lbl">Everything Else <span className="rc-badge">{unusedValues.length} Values, Counted One By One</span></p><ChipList values={unusedValues} getLabel={(value) => lookupLabelForColumn(section.breakdownColumn, value, section.model)} /></div>}</div>
                ) : <p className="rc-hint">Grouping does not apply while showing one overall number.</p>}
            </>
        );
    }

    function renderMetricGroup(section: MetricSection, group: MetricGroup) {
        const options = lookupOptionsForColumn(section.breakdownColumn, section.model);
        const key = dropKey("group", section.id, "", group.id);
        return <div className={dropTarget === key ? "rc-groupbox rc-over" : "rc-groupbox"} key={group.id} data-rc-validation={`metric-group-${section.id}-${group.id}`} {...dropZoneProps(key, () => dropMetricValue(section.id, "group", undefined, group.id))}><div className="rc-rule-row"><input value={group.name} onChange={(event) => updateMetric(section.id, (current) => ({ ...current, groups: current.groups.map((item) => item.id === group.id ? { ...item, name: event.target.value } : item) }))} placeholder="Group Name" /><span className="rc-spacer" /><IconAction label="Delete Group" onClick={() => updateMetric(section.id, (current) => ({ ...current, groups: current.groups.filter((item) => item.id !== group.id) }))} /></div><ChipList values={group.values} onRemove={(value) => updateMetric(section.id, (current) => ({ ...current, groups: current.groups.map((item) => item.id === group.id ? { ...item, values: removeChipValue(item.values, value) } : item) }))} emptyText="Drag Values Onto This Box" getLabel={(value) => lookupLabelForColumn(section.breakdownColumn, value, section.model)} /><ValuePicker allowCustom={isCalculatedColumnKey(section.breakdownColumn)} options={options.filter((option) => !group.values.includes(option.value))} onPick={(value) => updateMetric(section.id, (current) => ({ ...current, groups: current.groups.map((item) => item.id === group.id ? { ...item, values: addChipValue(item.values, value) } : item) }))} /></div>;
    }

    function renderDetailColumns(section: MetricSection) {
        const columns = detailColumnsForModel(section.model);
        return <div><p className="rc-lbl">Choose Which Columns to Include</p><p className="rc-hint">Columns are loaded from DataModelColumn. Leave none checked to include every column. Check specific ones to narrow the output.</p><p className="rc-hint">Column order, header names, and formatting for the Excel sheet are set on the Excel Layout tab.</p><div className="rc-mini-actions"><button className="rc-minibtn" disabled={!columns.length} onClick={() => updateMetric(section.id, (current) => ({ ...current, selectedColumns: columns.map(dataColumnSelectionKey) }))}>Select All</button><button className="rc-minibtn" onClick={() => updateMetric(section.id, (current) => ({ ...current, selectedColumns: [] }))}>Clear</button></div>{columns.length ? <div className="rc-grid">{columns.map((column) => {
            const value = dataColumnSelectionKey(column);
            return <label className="rc-cb" key={value || column.key}><input type="checkbox" checked={section.selectedColumns.includes(value) || section.selectedColumns.includes(column.key)} onChange={(event) => updateMetric(section.id, (current) => ({ ...current, selectedColumns: event.target.checked ? addChipValue(current.selectedColumns, value) : removeChipValue(removeChipValue(current.selectedColumns, value), column.key) }))} /> {column.label}</label>;
        })}</div> : <p className="rc-hint">No visible DataModelColumn records loaded for this data model.</p>}</div>;
    }

    // Full Row Detail: any number of categorization rule sets, each adding its own column to
    // the detail rows under a column name (alias) the user can set - see logic/detailRuleSets.ts.
    // Order here is the default column order; the Excel Layout tab can still reorder, rename
    // and format each column on the sheet.
    function renderDetailRuleSetPicker(report: ReportConfig, section: MetricSection) {
        if (!report.bucketRuleSets.length) {
            return <div className="rc-info-box" data-rc-validation={`metric-${section.id}`}><p className="rc-lbl">Categorization Columns</p><p className="rc-hint">No custom bucket Rule Sets exist yet. Create one in the Custom Bucket Categorization section, then pick it here to add it as a column.</p></div>;
        }
        const selectedIds = selectedDetailRuleSetIds(section);
        const isLegacy = !usesDetailRuleSetList(section) && selectedIds.length > 0;
        const outputs = detailRuleSetOutputs(report, usesDetailRuleSetList(section) ? section : { ...section, detailRuleSetIds: selectedIds });
        const available = report.bucketRuleSets.filter((set) => !selectedIds.includes(set.id));
        const setIds = (ids: string[]) => updateMetric(section.id, (current) => withDetailRuleSetIds(current, ids));
        const move = (index: number, direction: -1 | 1) => {
            const target = index + direction;
            if (target < 0 || target >= selectedIds.length) return;
            const next = [...selectedIds];
            [next[index], next[target]] = [next[target], next[index]];
            setIds(next);
        };
        // Renaming the column also renames its sheet header, unless the header was customized
        // on the Excel Layout tab (i.e. no longer matches the old column name).
        const setAlias = (ruleSetId: string, ruleSetName: string, alias: string) => updateMetric(section.id, (current) => {
            const base = usesDetailRuleSetList(current) ? current : withDetailRuleSetIds(current, selectedDetailRuleSetIds(current));
            const previousName = detailRuleSetDisplayName(base, ruleSetId, ruleSetName);
            const next = withDetailRuleSetAlias(base, ruleSetId, alias);
            const nextName = detailRuleSetDisplayName(next, ruleSetId, ruleSetName);
            if (!next.excelLayout) return next;
            return {
                ...next,
                excelLayout: {
                    ...next.excelLayout,
                    columns: next.excelLayout.columns.map((column) => {
                        if (column.sourceAlias !== detailRuleSetAlias(ruleSetId)) return column;
                        const header = column.detail?.header?.trim() || "";
                        const followsName = !header || header === previousName;
                        return { ...column, label: nextName, ...(column.detail && followsName ? { detail: { ...column.detail, header: nextName } } : {}) };
                    }),
                },
            };
        });
        const describe = (output: (typeof outputs)[number]) => {
            if (!output.ruleSet) return "This rule set no longer exists.";
            const parts: string[] = [];
            if (output.columnExpr) parts.push(`adds the "${output.label}" column`);
            if (output.filterClauses.length) parts.push("filters rows");
            return parts.length ? `${parts.join(", ")}.`.replace(/^./, (letter) => letter.toUpperCase()) : "No complete rules yet.";
        };
        return (
            <div className="rc-info-box rc-detail-rulesets" data-rc-validation={`metric-${section.id}`}>
                <p className="rc-lbl">Categorization Columns</p>
                <p className="rc-hint">Pick one or more Rule Sets. Each one is evaluated on its own for every row and adds its own column to the query. Give each column the name it should have in the query; leave it blank to use the Rule Set's name.</p>
                {isLegacy && <p className="rc-hint">This metric was set up with a single Rule Set. Changing the selection or a column name below adds each Rule Set as its own column.</p>}
                {outputs.length ? (
                    <ol className="rc-detail-ruleset-list">
                        {outputs.map((output, index) => (
                            <li className="rc-detail-ruleset-item" key={output.ruleSetId}>
                                <span className="rc-detail-ruleset-order">{index + 1}</span>
                                <span className="rc-detail-ruleset-copy">
                                    <b>{output.ruleSet ? output.ruleSet.name || "Untitled Rule Set" : "Missing rule set"}</b>
                                    <small>{describe(output)}</small>
                                </span>
                                <label className="rc-detail-ruleset-alias">
                                    <span>Column name</span>
                                    <input
                                        value={detailRuleSetAliasText(section, output.ruleSetId)}
                                        placeholder={output.ruleSet?.name || "Column name"}
                                        maxLength={DETAIL_RULE_SET_ALIAS_MAX_LENGTH}
                                        aria-label={`Column name for ${output.ruleSet?.name || "rule set"}`}
                                        disabled={!output.ruleSet}
                                        onChange={(event) => setAlias(output.ruleSetId, output.ruleSet?.name || "", event.target.value)}
                                    />
                                </label>
                                <button className="rc-minibtn" type="button" aria-label={`Move ${output.label} up`} disabled={index === 0} onClick={() => move(index, -1)}>Up</button>
                                <button className="rc-minibtn" type="button" aria-label={`Move ${output.label} down`} disabled={index === outputs.length - 1} onClick={() => move(index, 1)}>Down</button>
                                <IconAction label={`Remove ${output.label}`} onClick={() => setIds(selectedIds.filter((id) => id !== output.ruleSetId))} />
                            </li>
                        ))}
                    </ol>
                ) : <p className="rc-hint">No Rule Sets selected - the rows are returned without categorization columns.</p>}
                {available.length > 0 && (
                    <SelectPicker
                        options={[{ id: "none", value: "", name: "Add a Rule Set..." }, ...available.map((set) => ({ id: set.id, value: set.id, name: set.name || "Untitled Rule Set" }))]}
                        value=""
                        onChange={(value) => { if (value) setIds([...selectedIds, value]); }}
                        searchPlaceholder="Search Rule Sets..."
                    />
                )}
                {outputs.some((output) => output.columnExpr) && (
                    <p className="rc-hint">Columns added to the query: {outputs.filter((output) => output.columnExpr).map((output) => output.label).join(", ")}. Set their order, headers and format on the Excel Layout tab.</p>
                )}
            </div>
        );
    }

    function renderMetricRulePicker(report: ReportConfig, section: MetricSection) {
        const chosen = report.bucketRuleSets.find((set) => set.id === section.ruleSetId);
        if (!report.bucketRuleSets.length) return <div className="rc-info-box"><p className="rc-lbl">Categorization Rules</p><p className="rc-hint">No custom bucket Rule Sets exist yet. Create one in the Custom Bucket Categorization section first.</p></div>;
        return <div className="rc-info-box"><p className="rc-lbl">Which Rule Set to Use</p><p className="rc-hint">Rule Sets are defined once and can be reused by any metric.</p><SelectPicker options={[{ id: "none", value: "", name: "Choose a Rule Set..." }, ...report.bucketRuleSets.map((set) => ({ id: set.id, value: set.id, name: set.name }))]} value={section.ruleSetId} onChange={(value) => updateMetric(section.id, (current) => ({ ...current, ruleSetId: value }))} searchPlaceholder="Search Rule Sets..." />{chosen ? <p className="rc-hint">Using <strong>{chosen.name}</strong>: {chosen.rules.length} categories{chosen.includeElse === false ? ", items without a matching rule will be hidden" : `, plus "${chosen.elseLabel || "default"}" for anything that does not match a rule`}.</p> : <p className="rc-hint">Pick a Rule Set to finish this metric.</p>}</div>;
    }

    function renderOtpRules(section: MetricSection) {
        if (!section.model) return null;
        const timeColumns = models[section.model]?.timestampColumns || [];
        const categoryColumns = columnsForModel(section.model);
        return <div className="rc-otp" data-rc-validation={`metric-otp-${section.id}`}><label className="rc-cb"><input type="checkbox" checked={section.noBreakdown} onChange={(event) => updateMetric(section.id, (current) => ({ ...current, noBreakdown: event.target.checked }))} /> Just one overall percentage - do not break it down by {columnLabel(section.breakdownColumn)}</label><p className="rc-lbl">Late-Arrival Rules</p><p className="rc-hint">Add one rule per trip type if timing rules differ.</p>{section.otpRules.map((rule) => <div className="rc-groupbox" key={rule.id}><div className="rc-rule-row"><span className="rc-hint">Only When</span><select value={rule.conditionLogic} onChange={(event) => updateMetric(section.id, (current) => ({ ...current, otpRules: current.otpRules.map((item) => item.id === rule.id ? { ...item, conditionLogic: event.target.value as "and" | "or" } : item) }))}><option value="and">All of These Match (AND)</option><option value="or">Any of These Match (OR)</option></select><span className="rc-spacer" /><IconAction label="Remove OTP Rule" onClick={() => updateMetric(section.id, (current) => ({ ...current, otpRules: current.otpRules.filter((item) => item.id !== rule.id) }))} /></div>{rule.conditions.map((condition) => renderOtpCondition(section, rule, condition, categoryColumns))}{!rule.conditions.length && <p className="rc-hint indent">No conditions. Applies to every trip.</p>}<AddActionButton className="compact indent" onClick={() => updateOtpRule(section.id, rule.id, (current) => ({ ...current, conditions: [...current.conditions, { id: nextId("otpcond"), column: "", values: [] }] }))}>Add Condition</AddActionButton><div className="rc-condition-row"><span>Late If</span><select value={rule.compareCol} onChange={(event) => updateOtpRule(section.id, rule.id, (current) => ({ ...current, compareCol: event.target.value }))}><option value="">Compare...</option>{timeColumns.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}</select><span>vs</span><select value={rule.againstCol} onChange={(event) => updateOtpRule(section.id, rule.id, (current) => ({ ...current, againstCol: event.target.value }))}><option value="">Against...</option>{timeColumns.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}</select><span>Differs By &gt;=</span><input type="number" min="0" value={rule.thresholdMinutes} onChange={(event) => updateOtpRule(section.id, rule.id, (current) => ({ ...current, thresholdMinutes: Number(event.target.value) || 0 }))} /><span>Minutes</span></div></div>)}<AddActionButton className="compact" onClick={() => updateMetric(section.id, (current) => ({ ...current, otpRules: [...current.otpRules, makeOtpRule()] }))}>Add Rule</AddActionButton><label className="rc-label-block">If a needed time is missing on a trip<select value={section.otpMissingHandling} onChange={(event) => updateMetric(section.id, (current) => ({ ...current, otpMissingHandling: event.target.value as MetricSection["otpMissingHandling"] }))}>{otpMissingHandlingOptions.map((option) => <option key={option.id} value={option.value}>{option.name}</option>)}</select></label></div>;
    }

    function renderOtpCondition(section: MetricSection, rule: OtpRule, condition: OtpCondition, columns: DataColumn[]) {
        const options = lookupOptionsForColumn(condition.column, section.model);
        const values = options.map((option) => option.value);
        const remainingOptions = options.filter((option) => !condition.values.includes(option.value));
        return <div className="rc-condition" key={condition.id}><div className="rc-condition-row"><select value={condition.column} onChange={(event) => updateOtpCondition(section.id, rule.id, condition.id, (current) => ({ ...current, column: event.target.value, values: [] }))}><option value="">Choose A Column...</option>{columns.map((column) => <option key={column.key} value={column.key}>{column.label}</option>)}</select>{condition.column && <ValuePicker options={remainingOptions} placeholder={remainingOptions.length ? "+ Add A Value..." : "All Values Added"} onPick={(value) => updateOtpCondition(section.id, rule.id, condition.id, (current) => ({ ...current, values: addChipValue(current.values, value) }))} />}<span className="rc-spacer" /><IconAction label="Remove OTP Condition" icon="remove_circle_outline" onClick={() => updateOtpRule(section.id, rule.id, (current) => ({ ...current, conditions: current.conditions.filter((item) => item.id !== condition.id) }))} /></div>{condition.column && <><div className="rc-mini-actions"><button className="rc-minibtn" onClick={() => updateOtpCondition(section.id, rule.id, condition.id, (current) => ({ ...current, values }))}>Select All</button><button className="rc-minibtn" onClick={() => updateOtpCondition(section.id, rule.id, condition.id, (current) => ({ ...current, values: [] }))}>Clear</button></div><ChipList values={condition.values} onRemove={(value) => updateOtpCondition(section.id, rule.id, condition.id, (current) => ({ ...current, values: removeChipValue(current.values, value) }))} emptyText="Add one or more values (matches ANY of them)" getLabel={(value) => lookupLabelForColumn(condition.column, value, section.model)} /></>}</div>;
    }

    function updateOtpRule(sectionId: string, ruleId: string, updater: (rule: OtpRule) => OtpRule) {
        updateMetric(sectionId, (section) => ({ ...section, otpRules: section.otpRules.map((rule) => rule.id === ruleId ? updater(rule) : rule) }));
    }

    function updateOtpCondition(sectionId: string, ruleId: string, conditionId: string, updater: (condition: OtpCondition) => OtpCondition) {
        updateOtpRule(sectionId, ruleId, (rule) => ({ ...rule, conditions: rule.conditions.map((condition) => condition.id === conditionId ? updater(condition) : condition) }));
    }

    function renderExtraBreakdownBlock(section: MetricSection) {
        if (section.displayAs === "detail") return null;
        const selectedColumns = [section.breakdownColumn, ...section.extraBreakdowns.filter((item) => !isPeriodDateExtra(item)).map((item) => item.column)];
        const available = section.model
            ? breakdownColumnsForModel(section.model).filter((column) =>
                isTimestampColumnForModel(section.model, dataColumnSelectionKey(column))
                || !selectedColumns.some((selected) => columnMatchesKey(column, selected)))
            : [];
        const selectedExtraColumn = available.some((column) => columnMatchesKey(column, extraBreakdownSelections[section.id])) ? extraBreakdownSelections[section.id] : "";
        return (
            <div className="rc-extra">
                <p className="rc-lbl">Also Break Down By (Optional)</p>
                <p className="rc-hint">Add more columns to get one combined row per unique combination. Date columns are grouped by a date part (year, quarter, month, week, or day) - add the same date column again to break down by more than one part, e.g. Year and Month.</p>
                {section.extraBreakdowns.map((extra) => renderExtraBreakdownEditor(section, extra))}
                <div className="rc-extra-add">
                    <div className="rc-extra-add-title">
                        <span className="material-icons md-16" aria-hidden="true">add_chart</span>
                        <span>Add Another Breakdown</span>
                    </div>
                    <div className="rc-extra-add-row">
                        <div className="rc-extra-picker-field">
                            <SelectPicker
                                disabled={!available.length}
                                options={available.map((column) => ({
                                    id: dataColumnSelectionKey(column),
                                    value: dataColumnSelectionKey(column),
                                    name: isTimestampColumnForModel(section.model, dataColumnSelectionKey(column)) ? `${column.label} (date)` : columnPickerLabel(column),
                                    sortOrder: column.sortOrder,
                                }))}
                                value={selectedExtraColumn}
                                placeholder={available.length ? "Select Breakdown Column..." : "No More Columns Available"}
                                searchPlaceholder="Search Breakdown Columns..."
                                onChange={(value) => setExtraBreakdownSelections((current) => ({ ...current, [section.id]: value }))}
                            />
                        </div>
                        <AddActionButton
                            disabled={!selectedExtraColumn}
                            onClick={() => {
                                const column = selectedExtraColumn;
                                if (column) {
                                    const isDateColumn = isTimestampColumnForModel(section.model, column);
                                    updateMetric(section.id, (current) => ({
                                        ...current,
                                        extraBreakdowns: [...current.extraBreakdowns, { id: nextId("extra"), column, groups: [], granularity: isDateColumn ? "year" : undefined }],
                                    }));
                                }
                                setExtraBreakdownSelections((current) => ({ ...current, [section.id]: "" }));
                            }}
                        >
                            Add Column
                        </AddActionButton>
                    </div>
                </div>
            </div>
        );
    }

    function renderExtraBreakdownEditor(section: MetricSection, extra: ExtraBreakdown) {
        if (isPeriodDateExtra(extra)) {
            // Managed from the Period Date card - shown here so the full GROUP BY is visible in one place.
            return (
                <div className="rc-groupbox rc-extra-editor" key={extra.id} data-rc-validation={`metric-extra-${section.id}-${extra.id}`}>
                    <div className="rc-rule-row">
                        <span className="rc-lbl">{extraBreakdownLabel(section, extra)}</span>
                        <span className="rc-spacer" />
                        <IconAction label="Remove Breakdown Column" icon="link_off" onClick={() => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.filter((item) => item.id !== extra.id) }))} />
                    </div>
                    <p className="rc-hint">Groups by the {(DATE_GRANULARITY_OPTIONS.find((option) => option.key === extra.granularity)?.label || "").toLowerCase()} of the {PERIOD_DATE_LABEL}. Set which date each row uses in the {PERIOD_DATE_LABEL} section below.</p>
                </div>
            );
        }
        const column = breakdownColumnForModel(section.model, extra.column);
        if (!column) return null;
        if (extra.granularity) {
            return (
                <div className="rc-groupbox rc-extra-editor" key={extra.id} data-rc-validation={`metric-extra-${section.id}-${extra.id}`}>
                    <div className="rc-rule-row">
                        <span className="rc-lbl">{column.label}</span>
                        <span className="rc-spacer" />
                        <IconAction label="Remove Breakdown Column" icon="link_off" onClick={() => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.filter((item) => item.id !== extra.id) }))} />
                    </div>
                    <label className="rc-label-block">
                        Group By
                        <select
                            value={extra.granularity}
                            onChange={(event) => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.map((item) => item.id === extra.id ? { ...item, granularity: event.target.value as DateGranularity } : item) }))}
                        >
                            {DATE_GRANULARITY_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                        </select>
                    </label>
                    <p className="rc-hint">Adds {(DATE_GRANULARITY_OPTIONS.find((option) => option.key === extra.granularity)?.label || "").toLowerCase()} as its own column in the query and groups by it.</p>
                </div>
            );
        }
        const options = lookupOptionsForColumn(extra.column, section.model);
        const values = options.map((option) => option.value);
        const groupedValues = new Set<string>();
        extra.groups.forEach((group) => group.values.forEach((value) => groupedValues.add(value)));
        const unusedValues = values.filter((value) => !groupedValues.has(value));
        const newGroupKey = dropKey("newgroup", section.id, extra.id);
        return (
            <div className="rc-groupbox rc-extra-editor" key={extra.id} data-rc-validation={`metric-extra-${section.id}-${extra.id}`}>
                <div className="rc-rule-row"><span className="rc-lbl">{column.label}</span><span className="rc-spacer" /><IconAction label="Remove Breakdown Column" icon="link_off" onClick={() => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.filter((item) => item.id !== extra.id) }))} /></div>
                <div className="rc-palette">{options.map((option) => {
                    const value = option.value;
                    const used = groupedValues.has(value);
                    return (
                        <span
                            className={used ? "rc-chip rc-draggable rc-used" : "rc-chip rc-draggable"}
                            draggable
                            key={option.id}
                            onDragEnd={() => { setDragPayload(null); setDropTarget(null); }}
                            onDragStart={() => setDragPayload({ value, metricId: section.id, extraId: extra.id })}
                            role="button"
                            tabIndex={0}
                        >
                            {option.name}
                        </span>
                    );
                })}</div>
                <p className="rc-hint">Drag values onto a box below to combine them. Anything not grouped shows up on its own.</p>
                {extra.groups.map((group) => {
                    const key = dropKey("group", section.id, extra.id, group.id);
                    return <div className={dropTarget === key ? "rc-groupbox rc-over" : "rc-groupbox"} key={group.id} data-rc-validation={`metric-extra-group-${section.id}-${extra.id}-${group.id}`} {...dropZoneProps(key, () => dropMetricValue(section.id, "group", extra.id, group.id))}><div className="rc-rule-row"><input value={group.name} onChange={(event) => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.map((item) => item.id === extra.id ? { ...item, groups: item.groups.map((existing) => existing.id === group.id ? { ...existing, name: event.target.value } : existing) } : item) }))} placeholder="Group Name" /><span className="rc-spacer" /><IconAction label="Delete Breakdown Group" onClick={() => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.map((item) => item.id === extra.id ? { ...item, groups: item.groups.filter((existing) => existing.id !== group.id) } : item) }))} /></div><ChipList values={group.values} onRemove={(value) => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.map((item) => item.id === extra.id ? { ...item, groups: item.groups.map((existing) => existing.id === group.id ? { ...existing, values: removeChipValue(existing.values, value) } : existing) } : item) }))} emptyText="Drag Values Onto This Box" getLabel={(value) => lookupLabelForColumn(extra.column, value, section.model)} /><ValuePicker allowCustom={isCalculatedColumnKey(extra.column)} options={options.filter((option) => !group.values.includes(option.value))} onPick={(value) => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.map((item) => item.id === extra.id ? { ...item, groups: item.groups.map((existing) => existing.id === group.id ? { ...existing, values: addChipValue(existing.values, value) } : existing) } : item) }))} /></div>;
                })}
                <div className={dropTarget === newGroupKey ? "rc-newgroupzone rc-over" : "rc-newgroupzone"} {...dropZoneProps(newGroupKey, () => dropMetricValue(section.id, "newgroup", extra.id))} onClick={() => updateMetric(section.id, (current) => ({ ...current, extraBreakdowns: current.extraBreakdowns.map((item) => item.id === extra.id ? { ...item, groups: [...item.groups, { id: nextId("group"), name: "New Group", values: unusedValues.slice(0, 1) }] } : item) }))} role="button" tabIndex={0}>+ Drop A Value Here To Start A New Group</div>
                {extra.groups.length > 0 && unusedValues.length > 0 && <div className="rc-leftoverbox"><p className="rc-lbl">Everything Else <span className="rc-badge">{unusedValues.length} Values</span></p><ChipList values={unusedValues} getLabel={(value) => lookupLabelForColumn(extra.column, value, section.model)} /></div>}
            </div>
        );
    }


    // --- Period Date ------------------------------------------------------------------
    // Which date each row is dated by, chosen from conditions on other columns (first
    // matching rule wins), then grouped by its Year / Month / ... through ordinary extra
    // breakdowns with source "periodDate". See logic/periodDate.ts.
    function updatePeriodDateCase(sectionId: string, caseId: string, updater: (item: PeriodDateCase) => PeriodDateCase) {
        updateMetric(sectionId, (current) => current.periodDate
            ? { ...current, periodDate: { ...current.periodDate, cases: current.periodDate.cases.map((item) => item.id === caseId ? updater(item) : item) } }
            : current);
    }

    function periodDateCaseTreeCtx(report: ReportConfig, section: MetricSection, item: PeriodDateCase): RuleTreeCtx {
        const scope = { ...report, models: section.model ? [section.model] : [] };
        const currentCase = (current: MetricSection) => current.periodDate?.cases.find((candidate) => candidate.id === item.id);
        return {
            scope,
            columns: metricFilterColumns(section),
            timeColumns: reportTimeComparableColumns(scope),
            nodes: item.conditions,
            logic: item.conditionLogic === "or" ? "or" : "and",
            updateNodes: (updater) => updateMetric(section.id, (current) => {
                const existing = currentCase(current);
                if (!current.periodDate || !existing) return current;
                return { ...current, periodDate: { ...current.periodDate, cases: current.periodDate.cases.map((candidate) => candidate.id === item.id ? { ...candidate, conditions: updater(existing.conditions) } : candidate) } };
            }),
            setRootLogic: (logic) => updatePeriodDateCase(section.id, item.id, (current) => ({ ...current, conditionLogic: logic })),
            restrictOperatorsByType: true,
        };
    }

    function addPeriodDateCase(section: MetricSection, filterColumns: DataColumn[]) {
        updateMetric(section.id, (current) => {
            if (!current.periodDate) return current;
            const usedCaseIds = new Set(current.periodDate.cases.map((item) => item.id));
            const item = makePeriodDateCase(reserveUniqueId("pdcase", usedCaseIds));
            const condition = makeRuleCondition();
            condition.id = reserveUniqueId("cond", new Set(current.periodDate.cases.flatMap((existing) => Array.from(ruleNodeIds(existing.conditions)))));
            condition.column = dataColumnSelectionKey(filterColumns[0]) || filterColumns[0]?.key || "";
            condition.operator = operatorForColumnChange(filterColumns[0], "in");
            return { ...current, periodDate: { ...current.periodDate, cases: [...current.periodDate.cases, { ...item, conditions: [condition] }] } };
        });
    }

    // --- Per-metric reporting period override -----------------------------------------
    // Ticked: the report's reporting period (Setup) is NOT applied to this metric, and its own
    // condition tree is applied instead - same builder as Inclusion Criteria, so a date can be
    // compared to a typed date, another date column or Today. No conditions = no date
    // filter at all. Mirrors ReportSqlBuilder.MetricPeriodFilterClauses.
    function metricPeriodFilterTreeCtx(report: ReportConfig, section: MetricSection): RuleTreeCtx {
        const scope = { ...report, models: section.model ? [section.model] : [] };
        return {
            scope,
            columns: metricFilterColumns(section),
            timeColumns: reportTimeComparableColumns(scope),
            nodes: section.periodFilter?.conditions || [],
            logic: metricPeriodFilterLogic(section),
            updateNodes: (updater) => updateMetric(section.id, (current) => current.periodFilter
                ? { ...current, periodFilter: { ...current.periodFilter, conditions: updater(current.periodFilter.conditions || []) } }
                : current),
            setRootLogic: (logic) => updateMetric(section.id, (current) => current.periodFilter ? { ...current, periodFilter: { ...current.periodFilter, conditionLogic: logic } } : current),
            restrictOperatorsByType: true,
        };
    }

    function toggleMetricPeriodFilter(report: ReportConfig, section: MetricSection, checked: boolean, filterColumns: DataColumn[]) {
        updateMetric(section.id, (current) => {
            if (!checked) {
                const { periodFilter, ...rest } = current;
                return rest as MetricSection;
            }
            // Seed one condition on the report's anchor date for this model, e.g. "Trip Date >= ...".
            const dateColumns = periodDateColumns(current);
            const anchorKey = current.model ? report.modelBasedOn[current.model] || "" : "";
            const anchor = dateColumns.find((column) => columnMatchesKey(column, anchorKey)) || dateColumns[0] || filterColumns[0];
            const condition = makeRuleCondition();
            condition.id = reserveUniqueId("cond", new Set<string>());
            condition.column = dataColumnSelectionKey(anchor) || anchor?.key || "";
            condition.operator = operatorForColumnChange(anchor, "gte");
            return { ...current, periodFilter: { ...makeMetricPeriodFilter(), conditions: anchor ? [condition] : [] } };
        });
    }

    function renderMetricPeriodFilterBlock(report: ReportConfig, section: MetricSection, filterColumns: DataColumn[]) {
        if (!section.model) return null;
        const enabled = metricOverridesReportPeriod(section);
        const ctx = metricPeriodFilterTreeCtx(report, section);
        const outline = enabled ? metricPeriodFilterOutline(report, section) : "";
        const issues = enabled ? metricPeriodFilterIssues(report, section) : [];
        return (
            <div className="rc-filter-line rc-metric-period-filter" data-rc-validation={`metric-period-filter-${section.id}`}>
                <label className="rc-cb">
                    <input type="checkbox" checked={enabled} onChange={(event) => toggleMetricPeriodFilter(report, section, event.target.checked, filterColumns)} /> Don't apply the report's reporting period to this metric
                </label>
                {!enabled && <p className="rc-hint rc-metric-filter-teaser">Tick this to remove the Setup reporting period from this metric's query and define its own date criteria instead.</p>}
                {enabled && (
                    <div className="rc-metric-filter">
                        <div className="rc-rule-row rc-metric-filter-head">
                            <span className="rc-hint">Only rows matching these criteria are included - in place of the report's reporting period. Compare a date to a typed date, another date column, or Today (the server date). Remove every condition to apply no date filter.</span>
                            {ctx.nodes.length > 1 && (
                                <ValuePicker
                                    mode="select"
                                    options={RULE_LOGIC_OPTIONS}
                                    placeholder={ruleLevelLogicPlaceholder(ctx.nodes, ctx.logic)}
                                    searchPlaceholder="Search Logic..."
                                    onPick={(value) => setRuleLevelUniformJoin(ctx, "", value as "and" | "or")}
                                />
                            )}
                        </div>
                        {outline && <p className="rc-rule-outline"><span className="rc-lbl">Logic</span> <code>{outline}</code></p>}
                        {!ctx.nodes.length && <p className="rc-hint">No date criteria - this metric reads every date.</p>}
                        <RuleConditionEditor ctx={ctx} groupId={""} nodes={ctx.nodes} logic={ctx.logic} depth={0} reserveUniqueId={reserveUniqueId} lookupOptionsForReportColumn={lookupOptionsForReportColumn} lookupLabelForReportColumn={lookupLabelForReportColumn} />
                        {issues.length > 0 && <ul className="rc-rule-issues">{issues.map((issue) => <li key={`${issue.nodeId}-${issue.kind}`}>{issue.message}</li>)}</ul>}
                    </div>
                )}
            </div>
        );
    }

    function renderPeriodDateBlock(report: ReportConfig, section: MetricSection, filterColumns: DataColumn[]) {
        if (!section.model) return null;
        const dateColumns = periodDateColumns(section);
        const dateOptions = dateColumns.map((column) => ({ id: dataColumnSelectionKey(column), value: dataColumnSelectionKey(column), name: column.label, sortOrder: column.sortOrder }));
        const periodDate = section.periodDate;
        const isDetail = section.displayAs === "detail";
        if (!periodDate) {
            return (
                <div className="rc-extra rc-period-date" data-rc-validation={`metric-period-${section.id}`}>
                    <p className="rc-lbl">{PERIOD_DATE_LABEL} (Year / Month)</p>
                    <p className="rc-hint">Date each row by a different column depending on its values - e.g. cancelled trips by Cancelled Date, completed trips by Drop-off Time, everything else by Trip Date - then group by the year and month of that date.</p>
                    <AddActionButton
                        disabled={!dateColumns.length}
                        onClick={() => updateMetric(section.id, (current) => enablePeriodDate(report, current, nextId("extra"), nextId("extra")))}
                    >
                        Add {PERIOD_DATE_LABEL}
                    </AddActionButton>
                    {!dateColumns.length && <p className="rc-hint">This data model has no date columns.</p>}
                </div>
            );
        }
        const issues = periodDateIssues(report, section);
        const caseIds = new Set(periodDate.cases.map((item) => item.id));
        const nodeIdsByCase = new Map(periodDate.cases.map((item) => [item.id, ruleNodeIds(item.conditions)] as const));
        const issuesForCase = (item: PeriodDateCase) => issues.filter((issue) => issue.nodeId === item.id || issue.nodeId === `${item.id}-conditions` || nodeIdsByCase.get(item.id)?.has(issue.nodeId));
        const generalIssues = issues.filter((issue) => !caseIds.has(issue.nodeId) && !periodDate.cases.some((item) => issuesForCase(item).includes(issue)));
        const granularities = periodDateGranularities(section);
        const readBack = periodDateNarrative(report, section);
        const setPeriodDate = (patch: Partial<NonNullable<MetricSection["periodDate"]>>) => updateMetric(section.id, (current) => current.periodDate ? { ...current, periodDate: { ...current.periodDate, ...patch } } : current);
        return (
            <div className="rc-extra rc-period-date" data-rc-validation={`metric-period-${section.id}`}>
                <div className="rc-rule-row">
                    <p className="rc-lbl">{PERIOD_DATE_LABEL} (Year / Month)</p>
                    <span className="rc-spacer" />
                    <IconAction label={`Remove ${PERIOD_DATE_LABEL}`} onClick={() => updateMetric(section.id, (current) => removePeriodDate(current))} />
                </div>
                <p className="rc-hint">Use this date for each row. Rules are checked top to bottom and the first one that matches decides the date; rows matching none use the Otherwise date.</p>
                {periodDate.cases.map((item, index) => {
                    const ctx = periodDateCaseTreeCtx(report, section, item);
                    const caseIssues = issuesForCase(item);
                    return (
                        <div className="rc-groupbox rc-period-date-case" key={item.id}>
                            <div className="rc-rule-row">
                                <span className="rc-lbl">{index + 1}. When</span>
                                {ctx.nodes.length > 1 && (
                                    <ValuePicker
                                        mode="select"
                                        options={RULE_LOGIC_OPTIONS}
                                        placeholder={ruleLevelLogicPlaceholder(ctx.nodes, ctx.logic)}
                                        searchPlaceholder="Search Logic..."
                                        onPick={(value) => setRuleLevelUniformJoin(ctx, "", value as "and" | "or")}
                                    />
                                )}
                                <span className="rc-spacer" />
                                <IconAction label="Remove Date Rule" onClick={() => updateMetric(section.id, (current) => current.periodDate ? { ...current, periodDate: { ...current.periodDate, cases: current.periodDate.cases.filter((candidate) => candidate.id !== item.id) } } : current)} />
                            </div>
                            <RuleConditionEditor ctx={ctx} groupId={""} nodes={ctx.nodes} logic={ctx.logic} depth={0} reserveUniqueId={reserveUniqueId} lookupOptionsForReportColumn={lookupOptionsForReportColumn} lookupLabelForReportColumn={lookupLabelForReportColumn} />
                            <label className="rc-label-block">
                                Then Use This Date
                                <SelectPicker options={dateOptions} value={item.dateColumn} placeholder="Select A Date Column..." searchPlaceholder="Search Date Columns..." onChange={(value) => updatePeriodDateCase(section.id, item.id, (current) => ({ ...current, dateColumn: value }))} />
                            </label>
                            {caseIssues.length > 0 && <ul className="rc-rule-issues">{caseIssues.map((issue) => <li key={`${issue.nodeId}-${issue.message}`}>{issue.message}</li>)}</ul>}
                        </div>
                    );
                })}
                <AddActionButton className="compact" disabled={!filterColumns.length} onClick={() => addPeriodDateCase(section, filterColumns)}>Add Date Rule</AddActionButton>
                <label className="rc-label-block">
                    Otherwise Use
                    <SelectPicker options={dateOptions} value={periodDate.elseDateColumn} placeholder="Select A Date Column..." searchPlaceholder="Search Date Columns..." onChange={(value) => setPeriodDate({ elseDateColumn: value })} />
                </label>
                {periodDate.cases.length > 0 && (
                    <label className="rc-cb">
                        <input type="checkbox" checked={periodDate.fallbackWhenNull} onChange={(event) => setPeriodDate({ fallbackWhenNull: event.target.checked })} /> If the chosen date is empty, use the Otherwise date
                    </label>
                )}
                {!isDetail && (
                    <div className="rc-period-date-parts">
                        <p className="rc-lbl">Group By</p>
                        <div className="rc-grid">
                            {DATE_GRANULARITY_OPTIONS.map((option) => (
                                <label className="rc-cb" key={option.key}>
                                    <input
                                        type="checkbox"
                                        checked={granularities.includes(option.key)}
                                        onChange={(event) => updateMetric(section.id, (current) => setPeriodDateGranularity(current, option.key as DateGranularity, event.target.checked, nextId("extra")))}
                                    /> {option.label}
                                </label>
                            ))}
                        </div>
                        <p className="rc-hint">Each ticked part is added as its own column in the query and in the GROUP BY. They also appear under Also Break Down By, and can be used as the Excel time axis.</p>
                    </div>
                )}
                {isDetail && <p className="rc-hint">Full row detail is not grouped, so here the {PERIOD_DATE_LABEL} only decides which rows fall in the reporting period.</p>}
                <label className="rc-cb">
                    <input type="checkbox" checked={periodDate.applyToReportingPeriod} onChange={(event) => setPeriodDate({ applyToReportingPeriod: event.target.checked })} /> Also use this date for the reporting period
                </label>
                <p className="rc-hint">{periodDate.applyToReportingPeriod
                    ? "The report's date field is ignored for this metric - a row is in the period when its own Period Date is."
                    : "Rows are still filtered into the period by the report's date field; the Period Date only decides how they are grouped."}</p>
                {readBack && <p className="rc-rule-outline"><span className="rc-lbl">Reads As</span> <code>Dated {readBack}</code></p>}
                {generalIssues.length > 0 && <ul className="rc-rule-issues">{generalIssues.map((issue) => <li key={`${issue.nodeId}-${issue.message}`}>{issue.message}</li>)}</ul>}
            </div>
        );
    }

    /**
     * The two metric-level filters. Each is a full condition tree using the same builder as
     * a Categorization rule - multiple columns, per-junction AND/OR, nested groups - rather
     * than the single column and value list it used to be. The checkbox seeds one empty
     * condition when switched on and clears the tree when switched off; everything after
     * that is the shared editor.
     */
    function renderMetricFilters(report: ReportConfig, section: MetricSection, filterColumns: DataColumn[]) {
        return <>
            {renderMetricFilterGroup(
                report,
                section,
                "filter",
                filterColumns,
                "Inclusion Criteria",
                "Only rows matching these criteria are counted by this metric. Use any column on this model, combine conditions with AND or OR, and group them with Add Group to build nested expressions such as (A AND B) OR (C AND (D OR E)).",
            )}
            {renderMetricFilterGroup(
                report,
                section,
                "excludeFilter",
                filterColumns,
                "Exclusion Criteria",
                "Rows matching these criteria are removed from this metric, on top of whatever the inclusion criteria already narrowed down. The whole expression is negated, so \"A OR B\" leaves out rows matching either.",
            )}
        </>;
    }

    function renderMetricFilterGroup(
        report: ReportConfig,
        section: MetricSection,
        mode: MetricFilterMode,
        filterColumns: DataColumn[],
        title: string,
        hint: string,
    ) {
        const enabled = metricFilterEnabled(section, mode);
        const ctx = metricFilterTreeCtx(report, section, mode);
        const outline = metricFilterOutline(report, section, mode);
        const issues = metricFilterIssues(report, section, mode);
        return (
            <div className="rc-filter-line" data-rc-validation={`metric-filter-${section.id}-${mode}`}>
                <label className="rc-cb">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(event) => toggleMetricFilter(section, mode, event.target.checked, filterColumns)}
                    /> {title}
                </label>
                {!enabled && (
                    <p className="rc-hint rc-metric-filter-teaser">
                        Turn this on to build a condition over any column on this model - conditions combine with AND or OR, and groups let you nest them with parentheses.
                    </p>
                )}
                {enabled && (
                    <div className="rc-metric-filter">
                        <div className="rc-rule-row rc-metric-filter-head">
                            <span className="rc-hint">{hint}</span>
                            {ctx.nodes.length > 1 && (
                                <ValuePicker
                                    mode="select"
                                    options={RULE_LOGIC_OPTIONS}
                                    placeholder={ruleLevelLogicPlaceholder(ctx.nodes, ctx.logic)}
                                    searchPlaceholder="Search Logic..."
                                    onPick={(value) => setRuleLevelUniformJoin(ctx, "", value as "and" | "or")}
                                />
                            )}
                        </div>
                        {outline && <p className="rc-rule-outline"><span className="rc-lbl">Logic</span> <code>{outline}</code></p>}
                        {<RuleConditionEditor ctx={ctx} groupId={""} nodes={ctx.nodes} logic={ctx.logic} depth={0} reserveUniqueId={reserveUniqueId} lookupOptionsForReportColumn={lookupOptionsForReportColumn} lookupLabelForReportColumn={lookupLabelForReportColumn} />}
                        {issues.length > 0 && (
                            <ul className="rc-rule-issues">
                                {issues.map((issue) => <li key={`${issue.nodeId}-${issue.kind}`}>{issue.message}</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        );
    }

    /**
     * Switching a filter on seeds one empty condition pre-pointed at the first available
     * column, which is what the old checkbox did too. Switching it off clears the tree, and
     * setMetricFilterNodes clears the mirrored legacy fields with it - so an unchecked
     * filter leaves nothing behind that could still narrow the metric.
     */
    function toggleMetricFilter(section: MetricSection, mode: MetricFilterMode, checked: boolean, filterColumns: DataColumn[]) {
        updateMetric(section.id, (current) => {
            if (!checked) return setMetricFilterLogic(setMetricFilterNodes(current, mode, []), mode, mode === "filter" ? "and" : "or");
            const condition = makeRuleCondition();
            condition.id = reserveUniqueId("cond", ruleNodeIds(metricFilterNodes(current, mode)));
            condition.column = dataColumnSelectionKey(filterColumns[0]) || filterColumns[0]?.key || "";
            // Both sides seed with "Is Any Of"; the exclude side negates the whole tree rather than each condition.
            condition.operator = operatorForColumnChange(filterColumns[0], "in");
            return setMetricFilterNodes(current, mode, [condition]);
        });
    }

    // addChipValue / removeChipValue now live in components/ValueMultiSelect so the shared
    // value-entry control and every existing call site here apply identical add/remove rules.
    const dropKey = (zone: string, metricId: string, extraId = "", groupId = "") => [zone, metricId, extraId, groupId].join(":");

    const dropMetricValue = (metricId: string, zone: "include" | "exclude" | "newgroup" | "group", extraId?: string, groupId?: string) => {
        if (!dragPayload?.value || dragPayload.metricId !== metricId) return;
        const droppedValue = dragPayload.value;
        updateMetric(metricId, (section) => {
            if (extraId) {
                const extraBreakdowns = section.extraBreakdowns.map((extra) => {
                    if (extra.id !== extraId) return extra;
                    const groups = extra.groups.map((group) => ({ ...group, values: removeChipValue(group.values, droppedValue) }));
                    if (zone === "newgroup") return { ...extra, groups: [...groups, { id: nextId("group"), name: "New Group", values: [droppedValue] }] };
                    if (zone === "group" && groupId) return { ...extra, groups: groups.map((group) => group.id === groupId ? { ...group, values: addChipValue(group.values, droppedValue) } : group) };
                    return { ...extra, groups };
                });
                return { ...section, extraBreakdowns };
            }
            const groups = section.groups.map((group) => ({ ...group, values: removeChipValue(group.values, droppedValue) }));
            if (zone === "include") return { ...section, include: addChipValue(section.include, droppedValue), exclude: removeChipValue(section.exclude, droppedValue), groups };
            if (zone === "exclude") return { ...section, exclude: addChipValue(section.exclude, droppedValue), include: removeChipValue(section.include, droppedValue), groups };
            if (zone === "newgroup") return { ...section, groups: [...groups, { id: nextId("group"), name: "New Group", values: [droppedValue] }] };
            if (zone === "group" && groupId) return { ...section, groups: groups.map((group) => group.id === groupId ? { ...group, values: addChipValue(group.values, droppedValue) } : group) };
            return { ...section, groups };
        });
        setDragPayload(null);
        setDropTarget(null);
    };

    const dropZoneProps = (key: string, onDrop: () => void) => ({
        onDragOver: (event: React.DragEvent<HTMLElement>) => {
            event.preventDefault();
            setDropTarget(key);
        },
        onDragLeave: () => setDropTarget((current) => current === key ? null : current),
        onDrop: (event: React.DragEvent<HTMLElement>) => {
            event.preventDefault();
            onDrop();
        },
    });
    return renderMetricsTab(report);
}
